import ICreateProcedure from '@Shared.Angular/@types/core/contracts/commands/procedure/createProcedure';
import ICreateProcedureAction from '@Shared.Angular/@types/core/contracts/commands/procedure/createProcedureAction';
import { ApiDateTime } from '@Shared.Angular/@types/dateTime';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import angular from 'angular';

interface ScribeStep {
  order: number;
  title: string;
  description?: string;
  imageDataUrl?: string;
  overlayX?: number;
  overlayY?: number;
}

export default class ProcedureImportService {
  public static $inject = [];

  public parseScribeHtmlFiles(files: File[]): Promise<ICreateProcedure>[] {
    return files.map((file) => this.parseScribeHtmlFile(file));
  }

  public async parseScribeHtmlFile(file: File): Promise<ICreateProcedure> {
    const htmlContent = await this.readFileAsText(file);
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const titleElement = doc.querySelector('h1.scribe-title');
    const procedureName =
      titleElement?.textContent?.trim() || file.name.replace('.html', '');

    const description =
      doc.querySelector('p.scribe-description')?.textContent?.trim() || '';

    const creatorFullName = (
      doc.querySelector('p.scribe-author')?.textContent?.trim() || ''
    )
      .replace('Made by ', '')
      .replace(' with Scribe', '');

    const steps = await Promise.all(this.extractSteps(doc));

    const actions: ICreateProcedureAction[] = steps.map((step) => ({
      procedureId: EmptyGuid,
      businessId: EmptyGuid,
      userId: EmptyGuid,
      eventTime: new Date().toISOString() as ApiDateTime,
      image: step.imageDataUrl,
      mouseX: step.overlayX || 0,
      mouseY: step.overlayY || 0,
      elementType: '',
      elementX: 0,
      elementY: 0,
      elementWidth: 0,
      elementHeight: 0,
      elementText: '',
      elementPlaceHolder: '',
      title: step.title,
      description: step.description,
      order: step.order,
      uri: ''
    }));

    const procedure: ICreateProcedure = {
      businessId: EmptyGuid,
      userId: EmptyGuid,
      name: procedureName,
      description: description,
      creatorFullName: creatorFullName,
      categoryId: null,
      actions: actions
    };

    return procedure;
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }

  private extractSteps(doc: Document): Promise<ScribeStep>[] {
    const stepPromises: Promise<ScribeStep>[] = [];
    const stepElements = doc.querySelectorAll('div.scribe-step');
    let currentOrder = 0;

    stepElements.forEach((stepElement) => {
      const textElements = stepElement.querySelectorAll('p.scribe-step-text');
      if (textElements.length === 0) return;

      const textElement = textElements[0];
      const titleText = textElement.textContent?.trim() || '';
      const isWarning = textElement.classList.contains('scribe-step-warning');
      const isTip = textElement.classList.contains('scribe-step-tip');

      if (isWarning || isTip) {
        return;
      }

      const laterTexts = Array.from(textElements).slice(1);

      const step: ScribeStep = {
        order: ++currentOrder,
        title: this.extractStepTitle(titleText),
        description: laterTexts
          .map((t) => `<p>${t.textContent?.trim() || ''}</p>`)
          .join('')
      };
      const stepPromise = new Promise<ScribeStep>((resolve) => {
        const nextElement = stepElement.nextElementSibling;
        if (
          !nextElement ||
          !nextElement.classList.contains('scribe-screenshot-container')
        ) {
          resolve(step);
          return;
        }
        const imgElement = nextElement.querySelector('img.scribe-screenshot');
        if (!imgElement) {
          resolve(step);
          return;
        }
        const rawImageUrl = imgElement.getAttribute('src') || undefined;
        if (!rawImageUrl) {
          resolve(step);
          return;
        }

        const imageWithoutHighlightUrl = rawImageUrl.replace('wat=1', 'wat=0');
        const watPadMatch = rawImageUrl.match(/&wat_pad=(\d+),(\d+)/);
        if (watPadMatch) {
          // We have to offset the position so our overlay appears centered on the correct spot
          step.overlayX = parseInt(watPadMatch[1], 10) + 15;
          step.overlayY = parseInt(watPadMatch[2], 10) + 11;
        }

        fetch(imageWithoutHighlightUrl)
          .then((response) => response.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              step.imageDataUrl = reader.result as string;
              resolve(step);
            };
            reader.onerror = () => {
              resolve(step);
            };
            reader.readAsDataURL(blob);
          })
          .catch(() => {
            resolve(step);
          });
      });

      stepPromises.push(stepPromise);
    });

    return stepPromises;
  }

  private extractStepTitle(text: string): string {
    // Remove step numbers (e.g., "1. Click here" -> "Click here")
    const match = text.match(/^\d+\.\s*(.+)$/);
    return match ? match[1] : text;
  }
}

angular
  .module('flowingly.runner.services')
  .service('procedureImportService', ProcedureImportService);

export type ProcedureImportServiceType = InstanceType<
  typeof ProcedureImportService
>;
